import { useTranslation } from 'react-i18next'
import { formatNumber } from '@/lib/format'

export type Currency = 'dirty' | 'clean' | 'influence'

/**
 * THREE CURRENCIES, THREE FIXED COLOURS AND GLYPHS — law, not guidance.
 * A price never renders in a colour belonging to a currency it cannot be
 * paid in.
 */
const CURRENCY_GLYPH: Record<Currency, string> = { dirty: '◆', clean: '◇', influence: '◈' }
const CURRENCY_CLASS: Record<Currency, string> = { dirty: 'text-dirty', clean: 'text-clean', influence: 'text-influence' }

export interface CurrencyReadoutProps {
  currency: Currency
  amount: number | bigint
  /** Show the currency name as a label above. */
  labelled?: boolean
  size?: 'sm' | 'md' | 'lg'
  suffix?: string
  className?: string
}

export function CurrencyReadout({ currency, amount, labelled, size = 'md', suffix, className = '' }: CurrencyReadoutProps) {
  const { t } = useTranslation()
  const text = size === 'lg' ? 'text-[22px]' : size === 'sm' ? 'text-[13px]' : 'text-[15px]'
  const body = (
    <span
      className={`soc-num inline-flex items-baseline gap-1.5 ${text} ${CURRENCY_CLASS[currency]}`}
      title={t(`currency.${currency}`)}
    >
      <span aria-hidden>{CURRENCY_GLYPH[currency]}</span>
      <span>{formatNumber(amount)}</span>
      {suffix && <span className="text-[12px] text-ink-mute">{suffix}</span>}
      <span className="sr-only">{t(`currency.${currency}`)}</span>
    </span>
  )
  if (!labelled) return <span className={className}>{body}</span>
  return (
    <div className={className}>
      <div className="soc-label">{t(`currency.${currency}`)}</div>
      <div className="mt-1">{body}</div>
    </div>
  )
}
